import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";
import { clampFrozenBubblePosition } from "@/components/cursors/use-edge-aware-bubble-placement";
import type { Rect } from "@/lib/edge-aware-placement";
import { measureUntransformed } from "@/lib/measure-untransformed";
import { requestNativeGlassRefresh } from "./native-glass-settings";

const INTERACTIVE =
  'button,input,textarea,select,a,[role="button"],[role="textbox"],[contenteditable]:not([contenteditable="false"]),[data-no-drag]';
type Position = { left: number; top: number };

/** Moves a settled answer's or a question's bubble, preserving the cursor's
 *  own anchor. */
export function useCompactCursorPillDrag({
  enabled,
  resetKey,
  containerRef,
  onRect,
}: {
  enabled: boolean;
  resetKey?: string;
  containerRef: RefObject<HTMLDivElement | null>;
  onRect?: (rect: Rect) => void;
}) {
  const positionRef = useRef<Position | null>(null);
  const stopRef = useRef<(() => void) | null>(null);
  const suppressClick = useRef(false);
  const [dragging, setDragging] = useState(false);
  useLayoutEffect(() => {
    stopRef.current?.();
    positionRef.current = null;
    suppressClick.current = false;
    return () => stopRef.current?.();
  }, [enabled, resetKey]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!enabled || event.button !== 0 || stopRef.current) return;
      const target = event.target;
      if (!(target instanceof Element) || target.closest(INTERACTIVE)) return;
      const scroll = target.closest<HTMLElement>(
        '[data-testid="compact-cursor-pill-scroll"]',
      );
      if (scroll) {
        const rect = scroll.getBoundingClientRect();
        const scrollbar = scroll.offsetWidth - scroll.clientWidth;
        if (
          scroll.scrollHeight > scroll.clientHeight &&
          scrollbar > 0 &&
          event.clientX >= rect.right - scrollbar
        )
          return;
        const horizontalScrollbar = scroll.offsetHeight - scroll.clientHeight;
        if (
          scroll.scrollWidth > scroll.clientWidth &&
          horizontalScrollbar > 0 &&
          event.clientY >= rect.bottom - horizontalScrollbar
        )
          return;
      }
      const container = containerRef.current;
      if (!container) return;
      const shell = event.currentTarget;
      const start = container.getBoundingClientRect();
      const pointerId = event.pointerId;
      const startX = event.clientX;
      const startY = event.clientY;
      let didDrag = false;
      const stop = () => {
        stopRef.current = null;
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", end);
        window.removeEventListener("pointercancel", end);
        window.removeEventListener("blur", stop);
        shell.removeEventListener("lostpointercapture", end);
        if (shell.hasPointerCapture?.(pointerId))
          shell.releasePointerCapture(pointerId);
        setDragging(false);
        if (didDrag) {
          suppressClick.current = true;
          window.setTimeout(() => {
            suppressClick.current = false;
          }, 0);
        }
      };
      const end = (next: PointerEvent) => {
        if (next.pointerId === pointerId) stop();
      };
      const move = (next: PointerEvent) => {
        if (next.pointerId !== pointerId) return;
        const dx = next.clientX - startX;
        const dy = next.clientY - startY;
        if (!didDrag && Math.hypot(dx, dy) < 4) return;
        if (!didDrag) {
          didDrag = true;
          setDragging(true);
          try {
            shell.setPointerCapture(pointerId);
          } catch {
            /* Window listeners retain the drag when capture is unavailable. */
          }
        }
        const box = measureUntransformed(container);
        const pane = container.closest<HTMLElement>("[data-canvas-viewport]");
        const viewport = pane?.getBoundingClientRect() ?? {
          left: 0,
          top: 0,
          right: window.innerWidth,
          bottom: window.innerHeight,
        };
        const position = clampFrozenBubblePosition(
          { left: start.left + dx, top: start.top + dy },
          box,
          viewport,
        );
        positionRef.current = position;
        container.style.transform = `translate(${position.left - box.left}px, ${position.top - box.top}px)`;
        onRect?.({
          ...position,
          right: position.left + box.width,
          bottom: position.top + box.height,
        });
        requestNativeGlassRefresh({
          forceSend: true,
          trackTransition: false,
        });
        next.preventDefault();
        next.stopPropagation();
      };
      stopRef.current = stop;
      window.addEventListener("pointermove", move, { passive: false });
      window.addEventListener("pointerup", end);
      window.addEventListener("pointercancel", end);
      window.addEventListener("blur", stop);
      shell.addEventListener("lostpointercapture", end);
      // Preserve a click/text selection until movement crosses the threshold.
      event.stopPropagation();
    },
    [containerRef, enabled, onRect],
  );

  return {
    positionRef,
    dragging,
    dragHandlers: {
      onPointerDown,
      onClickCapture: (event: React.MouseEvent<HTMLDivElement>) => {
        if (!suppressClick.current) return;
        suppressClick.current = false;
        event.preventDefault();
        event.stopPropagation();
      },
    },
  };
}
